import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapMarker } from '@angular/google-maps';
import { ScriptLoaderService } from '@app/shared/services/script-loader/script-loader.service';
import { environment } from '@env/environment';
import { LoadingSpinnerComponent } from '@ui';
import { Logger } from '@utils';

@Component({
  selector: 'sbnb-task-location-map',
  templateUrl: './task-location-map.component.html',
  standalone: true,
  imports: [GoogleMapsModule, GoogleMap, MapMarker, LoadingSpinnerComponent],
  host: {
    class: 'block h-full',
  },
})
export class TaskLocationMapComponent implements OnInit {
  private readonly scriptLoader = inject(ScriptLoaderService);

  // Input for the location coordinates
  location = input.required<{ latitude: number; longitude: number }>();

  // Google Maps API loaded state
  apiLoaded = signal<boolean | null>(null);

  // Map options
  mapOptions = computed(() => ({
    zoom: 15,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: true,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  }));

  // Center position for the map
  center = computed(() => ({
    lat: this.location().latitude,
    lng: this.location().longitude,
  }));

  // Marker options
  markerOptions = computed(() => {
    // Create a custom cleaner icon SVG with circular background
    const cleanerIcon = `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="#5046E5" stroke="white" stroke-width="3"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M14.5 15.5c0 3.035 2.465 5.5 5.5 5.5s5.5-2.465 5.5-5.5S23.035 10 20 10a5.503 5.503 0 0 0-5.5 5.5Zm1.5 0c0-2.205 1.795-4 4-4s4 1.795 4 4-1.795 4-4 4-4-1.795-4-4Zm10.57 10.91 1.015 3.785 1.45-.39-1.015-3.785a4.762 4.762 0 0 0-4.59-3.52H16.57a4.752 4.752 0 0 0-4.59 3.52l-1.015 3.785 1.45.39L13.43 26.41A3.253 3.253 0 0 1 16.57 24h6.86c1.47 0 2.76.99 3.14 2.41Z" fill="white"/>
      </svg>
    `;

    return {
      icon: {
        url: 'data:image/svg+xml,' + encodeURIComponent(cleanerIcon),
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20),
      },
      title: 'Cleaner Location',
      animation: google.maps.Animation.DROP,
    };
  });

  ngOnInit(): void {
    this.loadGoogleMapsApi();
  }

  private loadGoogleMapsApi(): void {
    // Check if Google Maps is already loaded
    if (window.google?.maps) {
      this.apiLoaded.set(true);
      return;
    }

    // Load Google Maps API using ScriptLoaderService
    this.scriptLoader
      .load({
        name: 'Google Maps',
        src: `https://maps.googleapis.com/maps/api/js?key=${environment.google.places.apiKey}`,
      })
      .subscribe({
        next: () => {
          this.apiLoaded.set(true);
        },
        error: (error) => {
          Logger.error('Failed to load Google Maps API', error);
          this.apiLoaded.set(false);
        },
      });
  }
}
